/** @format */

import {Box, FormControl, Select, TextField, Button} from '@mui/material';
import {styled} from '@mui/material/styles';
import TodayIcon from '@mui/icons-material/Today';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

export const FormContainer = styled(Box)(({theme}) => ({
  width: 640,
  minWidth: 640,
  backgroundColor: theme.palette.figma_surface?.main,
  borderRadius: '8px 0 0 8px',
  padding: theme.spacing(4),
  minHeight: 390,
}));

export const GridContainer = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: 'repeat(3, auto)',
  gap: theme.spacing(3),
  marginTop: theme.spacing(2),

  '& > *': {
    minWidth: 0,
  },

  '& .MuiChip-root': {
    maxWidth: '100%',
  },
}));

export const FieldLabel = styled(Box)(({theme}) => ({
  fontSize: '14px',
  fontWeight: 400,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const NeedToConnectMetrikaBadge = styled(Box)(({theme}) => ({
  borderRadius: '9px',
  color: '#FEFAFA',
  backgroundColor: '#6F3A3A',
  fontSize: '12px',
  lineHeight: '14px',
  fontWeight: 500,
  padding: theme.spacing(0.5, 1),
}));

export const GoToSettingButton = styled(Button)(({theme}) => ({
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: 400,
  backgroundColor: theme.palette.figma_gray_dark_4?.main,
  padding: theme.spacing(2, 4),
  marginTop: theme.spacing(2),

  '&:hover': {
    backgroundColor: '#3C3C3C',
  },
}));

export const DateRangeContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',

  '& .react-datepicker__input-container': {
    paddingRight: '0 !important',
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '12px 16px 12px 40px',
    fontSize: '14px',
    border: '1px solid #838489',
    borderRadius: '8px',
    color: theme.palette.text.primary,
    height: '32px',

    '&:hover': {
      borderColor: theme.palette.figma_primary?.main,
    },

    '&:disabled': {
      borderColor: theme.palette.figma_gray_dark_4?.main,
      color: '#666666',
      WebkitTextFillColor: '#666666',
      opacity: 0.5,
    },
  },
}));

export const StyledTextField = styled(TextField)(({theme}) => ({
  '& .MuiOutlinedInput-root': {
    color: theme.palette.secondary.main,
    height: '32px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',

    '& fieldset': {
      borderColor: `${theme.palette.figma_surface_muted?.main} !important`,
    },

    '&:hover fieldset': {
      borderColor: `${theme.palette.figma_primary?.main} !important`,
    },

    '&.Mui-focused fieldset': {
      borderColor: '#007AFF !important',
    },

    '&.Mui-disabled': {
      color: theme.palette.figma_surface_muted?.main,

      '& fieldset': {
        borderColor: `${theme.palette.figma_gray_dark_4?.main} !important`,
      },
    },
  },
}));

export const StyledFormControl = styled(FormControl)(() => ({
  '& .MuiInputLabel-root': {
    color: '#888888',
    '&.Mui-focused': {
      color: '#4A4A4A',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

export const StyledSelect = styled(Select)(({theme}) => ({
  color: theme.palette.figma_gray_light_4?.main,
  height: '32px',
  borderRadius: '8px',
  fontSize: '14px',
  border: `1px solid ${theme.palette.figma_surface_muted?.main}`,

  '&:hover': {
    borderColor: theme.palette.figma_primary?.main,
  },

  '&:active': {
    borderColor: theme.palette.figma_primary?.light,
  },

  '&.Mui-disabled': {
    borderColor: theme.palette.figma_gray_dark_4?.main,

    '& .MuiSelect-select': {
      WebkitTextFillColor: `${theme.palette.figma_surface_muted?.main} !important`,
    },
  },

  '& .MuiSelect-select': {
    padding: theme.spacing(3, 4),
    paddingRight: '40px !important',
    display: 'flex',
    alignItems: 'center',
    minHeight: 'unset',
    color: theme.palette.text.primary,
  },

  '& .MuiSelect-icon': {
    color: '#888888',
    right: '12px',
    fontSize: '20px',
  },
}));

export const SubmitButton = styled(PrimaryMediumButton)<{disabled?: boolean}>(
  ({disabled, theme}) => ({
    gridColumn: '1 / -1',
    marginTop: theme.spacing(4),
    height: '64px',
    padding: theme.spacing(3.5, 4),
    borderRadius: theme.spacing(2),
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 400,
    width: '100%',
    backgroundColor: disabled ? '#3C3C3C' : theme.palette.semantic_dark?.primary.main || '#3e88c5',
    color: disabled ? '#888888' : theme.palette.semantic_dark?.primary.on || '#f9fcfe',
    border: 'none',
    opacity: 1,
    textTransform: 'none',

    '&:hover': {
      backgroundColor: disabled
        ? '#3C3C3C'
        : theme.palette.semantic_dark?.primary.hover || '#0066CC',
      opacity: 1,
    },

    '&:disabled': {
      backgroundColor: '#3C3C3C',
      color: '#888888',
    },
  }),
);

export const OrderResearchButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'loading',
})<{loading?: boolean}>(({theme, loading}) => ({
  gridColumn: '1 / -1',
  marginTop: theme.spacing(4),
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  background: theme.palette.semantic_dark?.surface.surface1,
  border: `1px solid ${
    loading
      ? theme.palette.semantic_dark?.primary.active || '#83c0f5'
      : theme.palette.semantic_dark?.surface.onSurfaceLight
  }`,
  color: loading
    ? theme.palette.semantic_dark?.primary.active || '#83c0f5'
    : theme.palette.semantic_dark?.surface.onSurface,
  textTransform: 'none',
  cursor: loading ? 'default' : 'pointer',

  '&:hover': {
    background: loading
      ? theme.palette.semantic_dark?.surface.surface1
      : theme.palette.semantic_dark?.surface.surface2,
  },

  '&:disabled': loading
    ? {
        background: theme.palette.semantic_dark?.surface.surface1,
        color: theme.palette.semantic_dark?.primary.active || '#83c0f5',
        borderColor: theme.palette.semantic_dark?.primary.active || '#83c0f5',
        cursor: 'default',
      }
    : {
        background: '#3C3C3C',
        color: '#888888',
        borderColor: '#3C3C3C',
        cursor: 'default',
      },
}));

export const OrderResearchButtonContent = styled(Box, {
  shouldForwardProp: prop => prop !== 'loading',
})<{loading?: boolean}>(({theme, loading}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: loading ? 'center' : 'flex-start',
  gap: theme.spacing(1),
}));

export const OrderResearchButtonTitle = styled(Box)(() => ({
  fontSize: '16px',
  lineHeight: '20px',
  fontWeight: 400,
  color: 'inherit',
}));

export const OrderResearchButtonSubtext = styled(Box)(() => ({
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: 400,
  color: 'inherit',
}));

export const StyledMenuItem = {
  backgroundColor: 'transparent !important',
  color: '#FFFFFF !important',
  fontSize: '14px !important',
  minHeight: '40px !important',
  padding: '8px 16px !important',
  whiteSpace: 'normal !important',
  '&:hover': {
    backgroundColor: '#4A4A4A !important',
  },
  '&.Mui-selected': {
    backgroundColor: '#4A4A4A !important',
  },
  '&.Mui-selected:hover': {
    backgroundColor: '#555555 !important',
  },
};

export const StyledCheckbox = {
  color: '#888888 !important',
  padding: '0 !important',

  '&.Mui-checked': {
    color: '#007AFF !important',
  },

  '&:hover': {
    color: '#4A4A4A !important',
  },

  '&.Mui-checked:hover': {
    backgroundColor: 'transparent !important',
    color: '#0066CC !important',
  },

  '&.Mui-disabled': {
    color: '#666666 !important',
  },

  '& .MuiSvgIcon-root': {
    fontSize: '18px !important',
  },
};

export const DatePickerWrapper = styled(Box)(() => ({
  flex: 1,
  position: 'relative',
}));

export const DatePickerIcon = styled(TodayIcon)(() => ({
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#888888',
  fontSize: '18px',
  zIndex: 1,
  pointerEvents: 'none',
}));

export const DateSeparator = styled(Box)(({theme}) => ({
  width: '8px',
  height: '1px',
  backgroundColor: theme.palette.figma_surface_muted?.main,
}));

export const KpiContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(2),

  '& > *': {
    flex: 1,
  },
}));

export const LimitExhaustedOverlay = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  color: 'white',
  textAlign: 'center',
  padding: theme.spacing(2),
}));
